import { hasValue } from "./general";

export enum SortTypeEnum {
	none = 0,
	ascending = 1,
	descending = -1,
}

export function cmp<T = any>(a: T, b: T) {
	if (a > b) return 1;
	if (a < b) return -1;
	return 0;
}

/** Comparing properties on object type */
export const cmpProp =
	<TObj>(propFn: (obj: TObj) => TObj[keyof TObj]) =>
	(a: TObj, b: TObj) =>
		cmp(propFn(a), propFn(b));

export function sortItem<T>(sortType: SortTypeEnum, a: T, b: T): number {
	switch (sortType) {
		case SortTypeEnum.ascending:
			return cmp(a, b);
		case SortTypeEnum.descending:
			return -cmp(a, b);
		default:
			return 0;
	}
}

export const sortItemByPropFn =
	<TObj>(propFn: (obj: TObj) => unknown) =>
	(sortType: SortTypeEnum) =>
	(a: TObj, b: TObj) =>
		sortItem(sortType, propFn(a), propFn(b));

export const sortFn = sortItemByPropFn<any>((t) => t);

export interface SortDefinition<TObj> {
	propFn?: (obj: TObj) => any;
	sortType?: SortTypeEnum;
}

const identity = <T>(value: T) => value;

/**
 * Sorts in order
 * @param sortDefinitions Definitions of individual sorts
 */
export function sortOrder<TObj>(...sortDefinitions: SortDefinition<TObj>[]): (a: TObj, b: TObj) => number {
	const comparators = sortDefinitions
		.filter(hasValue)
		.map((sd) => sortItemByPropFn<TObj>(sd.propFn ?? identity)(sd.sortType ?? SortTypeEnum.ascending));

	return (a, b) => {
		let result = 0;
		for (const compare of comparators) {
			result = compare(a, b);
			if (result) break;
		}
		return result;
	};
}
